import React, { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faTimes,
  faInfoCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

// Props for the WizardContainer
interface WizardContainerProps {
  children: ReactNode;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  onDelete?: () => void; // Optional delete handler for edit mode
  isSubmitting?: boolean;
  canGoNext?: boolean;
  canSubmit?: boolean;
  title: string;
  nextButtonTooltip?: string;
  submitButtonTooltip?: string;
  isEdit?: boolean; // Indicates if we're in edit mode
}

/**
 * WizardContainer - Manages the overall structure and navigation for a multi-step wizard
 */
const WizardContainer: React.FC<WizardContainerProps> = ({
  children,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onCancel,
  onSubmit,
  onDelete,
  isSubmitting = false,
  canGoNext = true,
  canSubmit = true,
  title,
  nextButtonTooltip = "Please complete the required fields",
  submitButtonTooltip = "Please ensure all steps are complete",
  isEdit = false,
}) => {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-500"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Progress indicator */}
      <div className="pt-6 pb-3">
        <div className="flex items-center space-x-2">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full flex-1 ${
                index <= currentStep ? "bg-brand" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        <div className="mt-2 text-sm text-gray-500 text-right">
          Step {currentStep + 1} of {totalSteps}
        </div>
      </div>

      {/* Content area */}
      <div className="py-3">{children}</div>

      {/* Navigation */}
      <div className="pt-4 border-t border-gray-200  flex justify-between">
        {isEdit && isFirstStep && onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2" />
            Delete Journey
          </button>
        ) : (
          <button
            type="button"
            onClick={onPrevious}
            disabled={isFirstStep}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
              isFirstStep
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Previous
          </button>
        )}

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting || !canSubmit}
              className={`relative group px-4 py-2 text-sm font-medium rounded-md text-white ${
                isSubmitting || !canSubmit
                  ? "bg-brand/60 cursor-not-allowed"
                  : "bg-brand hover:bg-brand/80"
              }`}
            >
              {!canSubmit && !isSubmitting && (
                <div className="absolute opacity-0 group-hover:opacity-100 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 -top-10 left-1/2 transform -translate-x-1/2 transition-opacity duration-300 pointer-events-none">
                  <div className="absolute h-2 w-2 bg-gray-800 rotate-45 left-1/2 transform -translate-x-1/2 -bottom-1"></div>
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  {submitButtonTooltip}
                </div>
              )}
              {isSubmitting
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                  ? "Save Journey"
                  : "Create Journey"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              disabled={!canGoNext}
              className={`relative group flex items-center px-4 py-2 text-sm font-medium rounded-md text-white ${
                !canGoNext
                  ? "bg-brand/60 cursor-not-allowed"
                  : "bg-brand hover:bg-brand/80"
              }`}
            >
              {!canGoNext && (
                <div className="absolute opacity-0 group-hover:opacity-100 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 -top-10 left-1/2 transform -translate-x-1/2 transition-opacity duration-300 pointer-events-none">
                  <div className="absolute h-2 w-2 bg-gray-800 rotate-45 left-1/2 transform -translate-x-1/2 -bottom-1"></div>
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  {nextButtonTooltip}
                </div>
              )}
              Next
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardContainer;
